import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { ApiService } from './base-api/api.service';
import {
  BulkFlightDecisionResult,
  CreateFlightRequest,
  FlightBookingDetails,
  FlightConfirmationImportResult,
  FlightRequestDecision,
  FlightRequestDetail,
  FlightRequestStatus,
  FlightRequestSummary,
} from '../models/flight-request';

/** Student flight booking requests plus the reviewer queue and booking hand-off. */
@Injectable({ providedIn: 'root' })
export class FlightBookingApiService {
  private readonly api = inject(ApiService);

  // ---- Student -----------------------------------------------------------

  createRequest(request: CreateFlightRequest): Observable<FlightRequestDetail> {
    return this.api
      .post<{ data: FlightRequestDetail }>('flight-requests', request)
      .pipe(map((response) => response.data));
  }

  getMyRequests(): Observable<FlightRequestSummary[]> {
    return this.api
      .get<{ data: FlightRequestSummary[] }>('flight-requests/my-requests')
      .pipe(map((response) => response.data));
  }

  getMyRequest(id: number): Observable<FlightRequestDetail> {
    return this.api
      .get<{ data: FlightRequestDetail }>(`flight-requests/my-requests/${id}`)
      .pipe(map((response) => response.data));
  }

  cancelRequest(id: number): Observable<unknown> {
    return this.api.post(`flight-requests/my-requests/${id}/cancel`, {});
  }

  // ---- Reviewer ----------------------------------------------------------

  getRequestsForReview(status?: FlightRequestStatus | null, search?: string): Observable<FlightRequestSummary[]> {
    const params = new URLSearchParams();
    if (status) {
      params.set('status', status);
    }
    if (search) {
      params.set('search', search);
    }
    const query = params.toString();

    return this.api
      .get<{ data: FlightRequestSummary[] }>(`admin/flight-requests${query ? `?${query}` : ''}`)
      .pipe(map((response) => response.data));
  }

  getRequestForReview(id: number): Observable<FlightRequestDetail> {
    return this.api
      .get<{ data: FlightRequestDetail }>(`admin/flight-requests/${id}`)
      .pipe(map((response) => response.data));
  }

  decide(id: number, decision: FlightRequestDecision): Observable<FlightRequestDetail> {
    return this.api
      .put<{ data: FlightRequestDetail }>(`admin/flight-requests/${id}/decision`, decision)
      .pipe(map((response) => response.data));
  }

  bulkDecide(ids: number[], decision: FlightRequestDecision): Observable<BulkFlightDecisionResult> {
    return this.api
      .post<{ data: BulkFlightDecisionResult }>('admin/flight-requests/bulk-decision', {
        ids,
        decision: decision.decision,
        reasonCode: decision.reasonCode ?? null,
        reason: decision.reason ?? null,
      })
      .pipe(map((response) => response.data));
  }

  attachBooking(id: number, booking: FlightBookingDetails): Observable<FlightRequestDetail> {
    return this.api
      .put<{ data: FlightRequestDetail }>(`admin/flight-requests/${id}/booking`, booking)
      .pipe(map((response) => response.data));
  }

  exportRequests(status?: FlightRequestStatus | null): Observable<Blob> {
    const query = status ? `?status=${status}` : '';
    return this.api.get<Blob>(`admin/flight-requests/export${query}`, { responseType: 'blob' });
  }

  importConfirmations(file: File): Observable<FlightConfirmationImportResult> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    return this.api
      .post<{ data: FlightConfirmationImportResult }>('admin/flight-requests/import-confirmations', formData)
      .pipe(map((response) => response.data));
  }
}
